import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const DiseaseEditModal = ({ isOpen, onClose, onUpdate, disease, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    symptoms: '',
    treatment: '',
    prevalence: 'Modérée',
    zone: '',
    severity: 'Modérée',
    category: '',
    isContagious: false
  });

  useEffect(() => {
    if (disease) {
      setFormData({
        name: disease.name || '',
        description: disease.description || '',
        symptoms: disease.symptoms ? (Array.isArray(disease.symptoms) ? disease.symptoms.join(', ') : disease.symptoms) : '',
        treatment: disease.treatment || '',
        prevalence: disease.prevalence || 'Modérée',
        zone: disease.zone || '',
        severity: disease.severity || 'Modérée',
        category: disease.category || '',
        isContagious: disease.isContagious || false
      });
    }
  }, [disease]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convertir les symptômes en tableau
    const symptomsArray = formData.symptoms 
      ? formData.symptoms.split(',').map(s => s.trim()).filter(s => s.length > 0)
      : [];
    
    const diseaseData = {
      ...formData,
      symptoms: symptomsArray
    };
    
    onUpdate(disease.id, diseaseData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Modifier la Maladie</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Nom */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom de la maladie *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Symptômes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Symptômes (séparés par des virgules)
            </label>
            <input
              type="text"
              name="symptoms"
              value={formData.symptoms}
              onChange={handleChange}
              placeholder="Ex: Fièvre, Maux de tête, Fatigue"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Prévalence */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prévalence *
              </label>
              <select
                name="prevalence"
                value={formData.prevalence}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Faible">Faible</option>
                <option value="Modérée">Modérée</option>
                <option value="Élevée">Élevée</option>
                <option value="Très élevée">Très élevée</option>
              </select>
            </div>

            {/* Sévérité */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sévérité *
              </label>
              <select
                name="severity"
                value={formData.severity}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Faible">Faible</option>
                <option value="Modérée">Modérée</option>
                <option value="Élevée">Élevée</option>
                <option value="Critique">Critique</option>
              </select>
            </div>
          </div>

          {/* Traitement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Traitement
            </label>
            <textarea
              name="treatment"
              value={formData.treatment}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zone
            </label>
            <input
              type="text"
              name="zone"
              value={formData.zone}
              onChange={handleChange}
              placeholder="Ex: Kinshasa, Matadi"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Contagieux */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isContagious"
              checked={formData.isContagious}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-700">
              Maladie contagieuse
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DiseaseEditModal;

